import { ImageXIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import React from 'react';
import { Skeleton } from '../skeleton';
import { Text, TextSize } from '../text';

interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  /**
   * Whether the image should have the default border
   *
   * @optional
   * @type boolean
   * @default false
   */
  border?: boolean;
  /**
   * Whether the image should be contained within the container or cover it
   *
   * @optional
   * @type boolean
   * @default true
   */
  contain?: boolean;
  /**
   * Whether the image is broken
   *
   * @optional
   * @type boolean
   */
  isBroken?: boolean;
  /**
   * Whether the image is being uploaded
   *
   * @optional
   * @type boolean
   */
  isUploading?: boolean;
  /**
   * The name of the image (used for alt text)
   *
   * @required
   * @type string
   */
  name: string;
  /**
   * The shape of the image (use unset for no shape) requires consumers to set the aspect ratio.
   * For rectangle to have border radius, set contain to false.
   *
   * @optional
   * @type 'unset' | 'rectangle' | 'square'
   * @default 'rectangle'
   */
  shape?: 'unset' | 'rectangle' | 'square';
  /**
   * The image source URL
   *
   * @required
   * @type string
   */
  src: string | null;
  /**
   * Fallback image source URL to use if the main src fails to load
   *
   * @optional
   * @type string
   */
  fallbackSrc?: string | null;
  /**
   * Whether the image is being analyzed
   *
   * @optional
   * @type boolean
   */
  isAnalyzing?: boolean;
  /**
   * Whether the image is a thumbnail
   *
   * @optional
   * @type boolean
   */
  isThumbnail?: boolean;
  /**
   * The class name for the image
   *
   * @optional
   * @type string
   */
  imageClassName?: string;
}

const styles = {
  root: cva({
    base: 'relative flex-shrink-0 bg-white h-full w-full @container',
    variants: {
      contain: {
        true: 'object-contain',
        false: 'object-cover',
      },
      shape: {
        unset: 'pt-0',
        rectangle: 'pt-[56.25%]',
        square: 'pt-[100%]',
      },
    },
    defaultVariants: {
      contain: true,
      shape: 'unset',
    },
  }),

  errorIcon: cva({
    base: 'text-primary',
    variants: {
      isThumbnail: {
        true: 'h-12 w-12',
        false: 'h-24 w-24',
      },
    },
  }),
  image: cva({
    base: 'absolute inset-0 h-full w-full',
    variants: {
      isAnalyzing: {
        true: 'opacity-50',
        false: '',
      },
      shape: {
        unset: '',
        rectangle: '',
        square: '',
      },
      border: {
        true: 'border border-primary',
        false: '',
      },
    },
    defaultVariants: {
      isAnalyzing: false,
      shape: 'square',
      border: false,
    },
  }),
};

/**
 * Backoff schedule for reloading an image whose request failed. Covers the
 * window where a freshly signed URL points at an object that hasn't been
 * copied into storage yet (~30s total) without retrying dead assets forever.
 */
const RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000];

function Image(props: ImageProps) {
  const {
    border = false,
    className,
    contain: containProp = true,
    fallbackSrc,
    isAnalyzing,
    isBroken,
    isThumbnail = false,
    isUploading,
    name,
    shape = 'square',
    src,
    imageClassName,
    ...other
  } = props;

  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [isUsingFallback, setIsUsingFallback] = React.useState(src == null || src === '');
  const [retryAttempt, setRetryAttempt] = React.useState(0);
  const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  React.useEffect(() => {
    return () => clearTimeout(retryTimeoutRef.current);
  }, []);

  // Image URLs are presigned per request, so every poll response carries a
  // fresh signature for the same object. Only a change in the underlying
  // object key is a new asset; a query-string-only change must not reset
  // state, or one pending image in a grid re-skeletons every thumbnail.
  const srcIdentity = typeof src === 'string' ? src.split('?')[0] : src;

  React.useEffect(() => {
    if (srcIdentity != null && srcIdentity !== '') {
      setIsUsingFallback(false);
    }
    // A new src is a new asset — abandon any in-flight retry schedule and
    // clear a previous asset's exhausted-retries error so it can render.
    clearTimeout(retryTimeoutRef.current);
    setRetryAttempt(0);
    setIsError(false);
    setIsLoading(true);
  }, [srcIdentity]);

  const currentSrc = isUsingFallback ? fallbackSrc : src;
  const showError = isBroken || isError || currentSrc === 'NoSuchKey';

  const handleOnError = React.useCallback(() => {
    if (!isUsingFallback && fallbackSrc && currentSrc !== fallbackSrc) {
      setIsUsingFallback(true);
      setIsLoading(true);
    } else if (retryAttempt < RETRY_DELAYS_MS.length) {
      // A signed URL can be handed out before the object lands in storage
      // (e.g. the ISS pipeline copies resize variants moments after the API
      // signs them), so the request answers with an S3 XML error instead of
      // an image. Retry a bounded number of times with backoff before
      // declaring the asset broken. The <img> is remounted via `key` to force
      // a re-request — appending a cache-buster would break the presigned
      // signature.
      retryTimeoutRef.current = setTimeout(() => {
        setIsUsingFallback(src == null || src === '');
        setRetryAttempt((attempt) => attempt + 1);
      }, RETRY_DELAYS_MS[retryAttempt]);
    } else {
      setIsError(true);
      setIsLoading(false);
    }
  }, [isUsingFallback, fallbackSrc, currentSrc, retryAttempt, src]);

  const handleOnLoad = React.useCallback(() => {
    setIsLoading(false);

    // Release optimistic-preview object URLs once the image has rendered.
    // Only blob: URLs need revoking (a no-op for http(s) URLs), and jsdom
    // doesn't implement revokeObjectURL at all.
    if (src?.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(src);
    }
  }, [src]);

  const contain = !containProp && (shape === 'unset' || shape === 'rectangle') ? false : containProp;

  return (
    <div className={cx(styles.root({ contain, shape }), className)} {...other}>
      {showError && (
        <div
          className={cx(
            'absolute inset-0 flex flex-col items-center justify-center gap-8 bg-primary-disabled rounded-[inherit]',
            {
              shape: shape,
            },
          )}
        >
          <ImageXIcon className={cx(styles.errorIcon({ isThumbnail }))} />
          {!isThumbnail && (
            <Text
              className={cx(
                'text-center font-bold',
                // Hide text in small containers (auto thumbnail detection)
                // Container smaller than 200px = thumbnail behavior
                // This automatically hides text in small containers unless overridden
                'hidden @[200px]:block',
              )}
              size={TextSize.Lg}
            >
              Could not load asset
            </Text>
          )}
          {/* Render a hidden img wrapper without a src to avoid browser re-requesting the page for empty src */}
          {/* otherwise causes:
          An empty string ("") was passed to the src attribute.
          This may cause the browser to download the whole page again over the network.
          To fix this, either do not render the element at all or pass null to src instead of an empty string */}
          <img alt="" aria-hidden="true" className="absolute h-full w-full opacity-0" />
        </div>
      )}
      {!showError && (
        <>
          {currentSrc ? (
            <img
              // Remount per retry attempt so the browser re-requests the same
              // (presigned) URL after a failed load.
              key={retryAttempt}
              alt={name}
              className={cx(styles.image({ shape, border }), imageClassName)}
              src={currentSrc}
              style={{ objectFit: 'inherit' }}
              onError={handleOnError}
              onLoad={handleOnLoad}
            />
          ) : null}
          {(isLoading || isUploading || isAnalyzing) && (
            <Skeleton className="vzt-skeleton absolute inset-0 h-full rounded-[inherit]" />
          )}
        </>
      )}
    </div>
  );
}

export { Image };
export type { ImageProps };
